import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Wind, Heart, Anchor, ChevronDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  pixelTrack,
  ensureFbcFromFbclid,
  getCookie,
  newEventId,
} from '../lib/metaPixel';

// Landing dedicada exclusivamente para la masterclass 'el-primer-limite-volver-a-ti'.
// Estructura de conversión inspirada en el JSON de referencia (hero → identificación
// → cambio de paradigma → qué experimentar → aspiracional → sobre mí → cierre → FAQ),
// con identidad visual de Escuela Akáshica (blanco natural, marfil, dorado sutil,
// verde salvia, mucho espacio, sin dramatización).

const SLUG = 'el-primer-limite-volver-a-ti';

// Foto de Herminio: crop cerrado al rostro (gravity face, zoom 0.7), redondo 120px, aro dorado con CSS
const HERMINIO_FACE_URL = 'https://res.cloudinary.com/dpppkdhdk/image/upload/c_thumb,g_face,z_0.7,f_auto,q_auto,w_160,h_160,r_max/v1789039701/ChatGPT_Image_8_sept_2026_15_56_36_nz8xtf.png';

const BIO_IMG = 'https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto,w_800/v1778754633/ChatGPT_Image_7_may_2026_18_45_40_6_xvlyr6.png';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long',
      timeZone: 'Europe/Madrid',
    });
  } catch (_) { return ''; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
    });
  } catch (_) { return ''; }
};

export default function MasterclassPrimerLimite() {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const autoEnrollRan = useRef(false);

  // ============== Meta Pixel + CAPI (solo evento Lead en el CTA) ==============
  // El Pixel base + PageView automático están instalados en /public/index.html.
  // Aquí solo persistimos _fbc desde ?fbclid y helper para enviar CAPI del Lead.
  const sendCapiEvent = async (eventName, eventId, extra = {}) => {
    try {
      const fbp = getCookie('_fbp') || null;
      const fbc = ensureFbcFromFbclid() || null;
      await api.post('/meta/events', {
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        fbp,
        fbc,
        content_name: SLUG,
        ...extra,
      });
    } catch (_) {
      // Silencioso: el Pixel del navegador ya cubre el evento aunque CAPI falle.
    }
  };

  // Persiste _fbc si el usuario llega con ?fbclid= en la URL
  useEffect(() => {
    ensureFbcFromFbclid();
  }, []);
  // =============================================================================

  // Fetch masterclass config (public endpoint)
  useEffect(() => {
    api.get(`/masterclasses/${SLUG}`)
      .then(r => setCfg(r.data))
      .catch(() => setNotFound(true));
    if (user) {
      api.get(`/masterclasses/${SLUG}/me`)
        .then(r => setEnrolled(!!r.data.is_enrolled))
        .catch(() => {});
    }
  }, [user]);

  const dateStr = useMemo(() => fmtDate(cfg?.date_iso), [cfg]);
  const timeStr = useMemo(() => fmtTime(cfg?.date_iso), [cfg]);
  // La fecha se considera "sin programar" si viene vacía O si no es parseable
  // (defensa por si en BD hay un string legacy tipo "2026-09-12 19:00 Madrid(CEST)").
  const parsedDateValid = useMemo(() => {
    if (!cfg?.date_iso) return false;
    const t = new Date(cfg.date_iso).getTime();
    return !Number.isNaN(t);
  }, [cfg]);
  const isUnscheduled = !parsedDateValid;

  // Countdown ticking each second — driven by cfg.date_iso (editable from admin)
  useEffect(() => {
    if (!parsedDateValid) { setCountdown(null); return; }
    const target = new Date(cfg.date_iso).getTime();
    const compute = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ d, h, m, s, expired: diff === 0 });
    };
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [parsedDateValid, cfg?.date_iso]);

  const finalizeRedirect = async () => {
    if (user && !user.onboarding_completed) {
      try { await completeOnboarding(); } catch (_) {}
    }
    navigate(`/mi-camino?welcome=masterclass&mc=${SLUG}`);
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate(`/register?redirect=${encodeURIComponent(`/masterclass/${SLUG}?action=enroll`)}`);
      return;
    }
    setEnrolling(true);
    try {
      const res = await api.post(`/masterclasses/${SLUG}/enroll`);
      setEnrolled(true);
      if (res.data.already_enrolled) {
        toast.info('Ya tienes tu plaza reservada. Te llevamos a tu camino.');
      } else {
        toast.success('¡Plaza reservada! Te llevamos a tu camino.');
      }
      await finalizeRedirect();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No hemos podido completar la inscripción');
    } finally {
      setEnrolling(false);
    }
  };

  // Auto-enroll if returning from register with ?action=enroll
  useEffect(() => {
    if (!user || !cfg) return;
    if (autoEnrollRan.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'enroll' && !enrolled) {
      autoEnrollRan.current = true;
      handleEnroll();
    }
  }, [user, cfg, enrolled]);

  // Scroll to registration section
  const scrollToRegister = () => {
    const el = document.getElementById('registro');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCta = () => {
    // Dispara evento Lead (Pixel + CAPI) con event_id compartido para deduplicación.
    const leadEventId = newEventId('lead');
    pixelTrack('Lead', { content_name: SLUG, content_category: 'webinar' }, leadEventId);
    // No esperamos al fetch para no bloquear la apertura de WhatsApp.
    sendCapiEvent('Lead', leadEventId);

    // Si el admin ha configurado un canal de WhatsApp, ese es el destino directo.
    // No se crea inscripción en BD ni se pide login: se abre el enlace en nueva pestaña.
    const wa = (cfg?.whatsapp_channel_url || '').trim();
    if (wa) {
      window.open(wa, '_blank', 'noopener,noreferrer');
      return;
    }
    // Fallback (canal no configurado): flujo clásico de inscripción por email.
    if (enrolled) { finalizeRedirect(); return; }
    handleEnroll();
  };

  if (notFound) {
    return (
      <div className="pt-24 min-h-screen flex flex-col items-center justify-center bg-[#FDFCFA] px-6 text-center">
        <Sparkles className="w-10 h-10 text-[#c9a227]/50 mb-4" strokeWidth={1.25} />
        <h1 className="text-2xl font-heading text-[#2a2d24] mb-2">Este encuentro aún no está disponible</h1>
        <Link to="/formaciones" className="mt-4 inline-flex items-center gap-2 text-sm text-[#8a7428] hover:underline">
          Ver formaciones <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFA] text-[#2a2d24]">
      {/* ============== HERO ============== */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-5 sm:px-8 lg:px-16 bg-gradient-to-b from-[#FDFCFA] via-[#FDFCFA] to-[#faf7ee]">
        <div className="max-w-4xl mx-auto text-center">
          {/* Firma humana — foto pequeña + nombre */}
          <div className="inline-flex items-center gap-3 mb-5 sm:mb-6" data-testid="mc-hero-signature">
            <img
              src={HERMINIO_FACE_URL}
              alt="Herminio Vázquez"
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover ring-1 ring-[#c9a227]/50 shadow-sm"
              loading="eager"
            />
            <span className="text-[11px] sm:text-xs tracking-[0.15em] text-[#5a5d53] uppercase">
              Con Herminio Vázquez
            </span>
          </div>

          {/* Marca de comienzo */}
          <div className="w-8 h-px bg-[#c9a227]/60 mx-auto mb-5" aria-hidden />

          {/* Cintillo — badge/pill destacando que es gratuito */}
          <div className="flex justify-center mb-5 sm:mb-6" data-testid="mc-hero-eyebrow-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#faf3df] border border-[#c9a227]/50 text-[11px] sm:text-xs tracking-[0.22em] text-[#8a7428] uppercase font-medium shadow-[0_1px_2px_rgba(201,162,39,0.08)]"
              data-testid="mc-hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#c9a227]" aria-hidden />
              Encuentro online gratuito
            </span>
          </div>

          {/* Título como firma del encuentro (reducido) */}
          <p className="font-heading italic text-lg sm:text-xl lg:text-2xl text-[#3a3d33]/80 mb-5 sm:mb-7" data-testid="mc-hero-title">
            El primer límite: <span className="text-[#c9a227]">volver a ti</span>
          </p>

          {/* Frase clave — protagonista */}
          <h1 className="font-heading text-[26px] sm:text-5xl lg:text-6xl leading-[1.14] tracking-tight text-[#2a2d24] mb-5 sm:mb-7 max-w-3xl mx-auto" data-testid="mc-hero-question">
            Sientes que hay personas y ambientes que{' '}
            <span className="text-[#c9a227]">drenan tu energía</span>,{' '}
            <span className="text-[#c9a227]">tensan tu cuerpo</span> y{' '}
            <span className="text-[#c9a227]">te alejan de ti misma</span>.
          </h1>

          {/* Promesa (susurrada) */}
          <p className="text-sm sm:text-base lg:text-lg leading-relaxed text-[#5a5d53] max-w-2xl mx-auto mb-4 sm:mb-5" data-testid="mc-hero-promise">
            En este encuentro vas a aprender a reconocer el momento en que empiezas a perderte y a soltar lo que no es tuyo. Así podrás recuperar tu espacio interior y volver a sentirte anclada, libre y presente, incluso cuando el entorno es intenso o demandante.
          </p>

          {/* Fecha/hora — icon list estilo plantilla, adaptada Akáshica */}
          {!isUnscheduled && (
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-8 mb-6 sm:mb-8" data-testid="mc-hero-schedule">
              <span className="inline-flex items-center gap-2.5 text-sm sm:text-base text-[#2a2d24]">
                <Calendar className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
                <span className="capitalize font-medium">{dateStr}</span>
              </span>
              <span className="hidden sm:block w-px h-5 bg-[#c9a227]/30" aria-hidden />
              <span className="inline-flex items-center gap-2.5 text-sm sm:text-base text-[#2a2d24]">
                <Clock className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
                <span className="font-medium">{timeStr}</span>
                <span className="text-[#8a8d83] text-xs sm:text-sm">· hora de Madrid</span>
              </span>
            </div>
          )}

          {/* Countdown — solo cuando hay fecha y aún no ha llegado */}
          {countdown && !countdown.expired && (
            <div className="flex justify-center gap-2 sm:gap-3 mb-6 sm:mb-8" data-testid="mc-hero-countdown">
              {[
                { v: countdown.d, l: 'Días' },
                { v: countdown.h, l: 'Horas' },
                { v: countdown.m, l: 'Minutos' },
                { v: countdown.s, l: 'Segundos' },
              ].map((u, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center min-w-[60px] sm:min-w-[76px] px-2 py-2.5 sm:py-3 bg-[#faf7ee] border border-[#c9a227]/25 rounded-xl"
                >
                  <span className="font-heading text-2xl sm:text-3xl leading-none text-[#2a2d24] tabular-nums">
                    {String(u.v).padStart(2, '0')}
                  </span>
                  <span className="text-[9px] sm:text-[10px] tracking-[0.15em] uppercase text-[#8a7428] mt-1.5">
                    {u.l}
                  </span>
                </div>
              ))}
            </div>
          )}

          {countdown?.expired && (
            <p className="text-sm text-[#8a7428] mb-6 tracking-wide">
              El encuentro ha comenzado. Únete si tienes tu plaza reservada.
            </p>
          )}

          {isUnscheduled && (
            <p className="text-xs text-[#8a8d83] tracking-wide mb-6" data-testid="mc-hero-unscheduled">
              Fecha y hora se anunciarán pronto.
            </p>
          )}

          {/* CTA */}
          <button
            type="button"
            onClick={handleCta}
            disabled={enrolling}
            className="inline-flex items-center gap-2 bg-[#2a2d24] hover:bg-[#3a3d33] text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full text-sm sm:text-base font-medium tracking-wide transition-all duration-200 hover:shadow-lg disabled:opacity-60"
            data-testid="mc-hero-cta"
          >
            {enrolling ? 'Reservando…' : enrolled ? 'Ya tienes tu plaza · Ir a mi camino' : 'Reservar mi plaza gratis'}
            <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </button>

          {/* Nota bajo el botón */}
          <p className="text-[11px] text-[#8a8d83] mt-3 tracking-wide" data-testid="mc-hero-note">
            Plazas limitadas al aforo del directo
          </p>
        </div>
      </section>

      {/* ============== IDENTIFICACIÓN ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-[#f5f1e8]/40">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-center mb-12 sm:mb-16" data-testid="mc-recognize-title">
            ¿Te reconoces en esto?
          </h2>
          <ul className="space-y-6 text-base sm:text-lg leading-relaxed text-[#3a3d33] max-w-2xl mx-auto">
            {[
              'Estar con mucha gente me agota.',
              'Hay personas que me dejan drenada.',
              'Absorbo emociones que no son mías.',
              'Evito ciertos lugares o personas para no sentirme así después.',
              'Mi cuerpo se tensa con algunas personas.',
              'Necesito estar sola para volver a mí.',
              'A veces siento que me pierdo en la relación.',
            ].map((line, i) => (
              <li key={i} className="pl-6 border-l-2 border-[#c9a227]/40">
                <span className="italic">{line}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-sm sm:text-base text-[#5a5d53] mt-14 max-w-xl mx-auto">
            Si te reconoces aquí, este encuentro puede ser para ti.
          </p>
        </div>
      </section>

      {/* ============== CAMBIO DE PARADIGMA ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-tight mb-10 sm:mb-14" data-testid="mc-paradigm-title">
            Quizá el límite empieza mucho antes de decir <span className="italic">"no"</span>
          </h2>
          <div className="space-y-6 text-base sm:text-lg leading-relaxed text-[#3a3d33]">
            <p>
              Nos enseñaron que poner límites es decir que no, alejarnos, construir barreras o protegernos del otro.
            </p>
            <p>Pero hay un límite más esencial que sucede antes:</p>
            <p className="font-heading text-lg sm:text-xl text-[#2a2d24] italic border-l-2 border-[#c9a227] pl-6">
              El primer límite nace al permanecer contigo incluso cuando sientes profundamente al otro.
            </p>
            <p>
              Muchas veces el problema no es que sientas demasiado. Es que, mientras atiendes a lo que sucede fuera,
              puedes dejar de escuchar lo que está sucediendo dentro de ti.
            </p>
          </div>
          <p className="font-heading text-xl sm:text-2xl lg:text-3xl text-center mt-16 leading-snug text-[#c9a227]">
            ¿Y si pudieras sentir a los demás… sin dejar de sentirte tú?
          </p>
        </div>
      </section>

      {/* ============== QUÉ VAS A EXPERIMENTAR ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-[#f5f1e8]/40">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-center mb-14 sm:mb-20">
            Lo que vas a experimentar en este encuentro
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-14">
            {[
              { n: '01', icon: Wind, title: 'Detectar el momento en que empiezas a salir de ti', body: 'Antes de llegar al cansancio, bloqueo o saturación.' },
              { n: '02', icon: Heart, title: 'Diferenciar entre sentir al otro y fundirte con lo que percibes', body: 'Empezar a descubrir una relación diferente con tu sensibilidad.' },
              { n: '03', icon: Anchor, title: 'Práctica en directo: regresar a tu cuerpo y a tu propio espacio', body: 'Experimentarlo mientras sigues estando en relación con lo externo.' },
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-start gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-heading text-[#c9a227]/70">{step.n}</span>
                  <step.icon className="w-5 h-5 text-[#8a7428]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg sm:text-xl leading-snug text-[#2a2d24]">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-[#5a5d53]">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
          <p className="text-center italic text-base sm:text-lg text-[#3a3d33] mt-16 max-w-2xl mx-auto">
            No quiero que solamente lo entiendas. Quiero que puedas empezar a sentirlo en ti.
          </p>
          <div className="text-center mt-10">
            <button
              type="button"
              onClick={handleCta}
              disabled={enrolling}
              className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b8921f] text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
              data-testid="mc-cta-mid"
            >
              Reservar mi plaza gratis
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      {/* ============== ASPIRACIONAL ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-center mb-14">
            Imagina por un momento…
          </h2>
          <ul className="space-y-8 text-base sm:text-lg leading-relaxed text-[#3a3d33]">
            <li className="pl-6 border-l border-[#c9a227]/30">
              Estar en una comida familiar o entrar en un grupo y seguir sintiéndote tú: presente y conectada contigo.
            </li>
            <li className="pl-6 border-l border-[#c9a227]/30">
              Escuchar o acompañar a alguien sin acabar completamente agotada.
            </li>
            <li className="pl-6 border-l border-[#c9a227]/30">
              Vivir tu sensibilidad sin permanecer constantemente a la defensiva.
            </li>
            <li className="pl-6 border-l border-[#c9a227]/30">
              Poder volver a tu propio centro sin necesitar desaparecer del mundo para recuperarte.
            </li>
          </ul>
          <p className="font-heading text-xl sm:text-2xl lg:text-3xl leading-snug text-center mt-16 mb-4 text-[#2a2d24]">
            No necesitas apagar tu sensibilidad.
          </p>
          <p className="font-heading text-xl sm:text-2xl lg:text-3xl leading-snug text-center text-[#c9a227]">
            Necesitas aprender a no perderte a ti cuando estás con los demás.
          </p>
          <div className="text-center mt-12">
            <button
              type="button"
              onClick={handleCta}
              disabled={enrolling}
              className="inline-flex items-center gap-2 bg-[#2a2d24] hover:bg-[#3a3d33] text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
              data-testid="mc-cta-aspirational"
            >
              Reservar mi plaza gratis
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </section>

      {/* ============== SOBRE MÍ ============== */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 lg:px-16 bg-[#f5f1e8]/40">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-14 items-center">
          <div className="md:col-span-2">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#e8e4da]">
              <img src={BIO_IMG} alt="Herminio Vázquez" loading="lazy"
                   className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.25em] text-[#8a7428] uppercase mb-4">Sobre mí</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl mb-6" data-testid="mc-about-title">Herminio Vázquez</h2>
            <div className="space-y-4 text-base leading-relaxed text-[#3a3d33]">
              <p>
                Llevo más de una década acompañando procesos y espacios formativos alrededor de la consciencia, la energía y el cuerpo.
              </p>
              <p>
                Yo también viví durante mucho tiempo pensando que tenía que protegerme, cerrarme o aislar mi campo energético.
                Había ambientes con mucha gente y determinados vínculos en los que mi cuerpo se tensaba — especialmente hombros y cuello.
                Podía terminar cansado, bloqueado, con poca energía y necesitando estar solo para volver a sentirme yo.
              </p>
              <p>
                Por eso este trabajo no nace únicamente de algo que he estudiado o acompañado en otras personas.
                También nace de algo que he necesitado comprender y transformar en mí.
              </p>
              <p className="italic text-[#2a2d24]">
                Hoy mi propuesta ya no consiste en protegerme más del mundo.
                Quiero acompañarte a descubrir cómo estar en él, sentirlo y relacionarte sin dejarte atrás.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== CIERRE / REGISTRO ============== */}
      <section id="registro" className="py-24 sm:py-32 px-5 sm:px-8 lg:px-16 scroll-mt-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-tight mb-8">
            El primer paso empieza aquí
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-[#3a3d33] mb-12 max-w-2xl mx-auto">
            Si quieres explorar una forma diferente de vivir tu sensibilidad, sin agotarte, sin cerrarte, sin desaparecer de ti cuando estás con los demás:
          </p>
          <p className="font-heading text-xl sm:text-2xl mb-2">EL PRIMER LÍMITE: VOLVER A TI</p>
          <p className="text-sm text-[#8a7428] tracking-wide mb-10">
            Encuentro online gratuito · Experiencia en directo
          </p>

          {!isUnscheduled && (
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-[#5a5d53] mb-10">
              <span className="inline-flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#c9a227]" strokeWidth={1.5} />
                <span className="capitalize">{dateStr}</span>
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#c9a227]" strokeWidth={1.5} />
                {timeStr}
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={handleCta}
            disabled={enrolling}
            className="inline-flex items-center gap-2 bg-[#2a2d24] hover:bg-[#3a3d33] text-white px-10 sm:px-14 py-5 sm:py-6 rounded-full text-base sm:text-lg font-medium tracking-wide transition-colors disabled:opacity-60"
            data-testid="mc-cta-final"
          >
            {enrolling ? 'Reservando…' : enrolled ? 'Ya tienes tu plaza · Ir a mi camino' : 'Reservar mi plaza gratis'}
            <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
          </button>

          {!user && (
            <p className="text-xs text-[#8a8d83] mt-6">
              Solo necesitas tu nombre y correo. Sin coste.
            </p>
          )}
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 lg:px-16 bg-[#f5f1e8]/40">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-xl sm:text-2xl text-center mb-10">Preguntas frecuentes</h2>
          <div className="space-y-3">
            {[
              {
                q: '¿Necesito participar o hablar durante el encuentro?',
                a: 'No. Puedes vivir la experiencia respetando tu propio ritmo. Habrá espacios de práctica y acompañamiento, pero no necesitas exponerte ni compartir nada que no quieras compartir.',
              },
              {
                q: '¿Habrá grabación?',
                a: cfg?.recording_available_config
                  ? 'Sí. Todas las personas inscritas recibirán acceso a la grabación durante un tiempo limitado tras el encuentro.'
                  : 'Aún no está confirmado. Te lo comunicaremos antes del encuentro.',
              },
              {
                q: '¿Es solo para mujeres?',
                a: 'No. La comunicación está especialmente pensada para personas sensibles que se reconocen en esta experiencia. Aunque el lenguaje pueda dirigirse principalmente a mujeres, el encuentro no es excluyente.',
              },
            ].map((item, i) => (
              <details
                key={i}
                open={openFaq === i}
                onToggle={(e) => setOpenFaq(e.currentTarget.open ? i : null)}
                className="group bg-white/70 rounded-xl border border-[#e8e4da] px-5 py-4 transition-colors hover:bg-white"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-sm sm:text-base text-[#2a2d24] font-medium">{item.q}</span>
                  <ChevronDown className="w-4 h-4 text-[#8a7428] transition-transform group-open:rotate-180" strokeWidth={1.5} />
                </summary>
                <p className="text-sm text-[#5a5d53] mt-3 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
